import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';

import { Comment } from 'src/app/models/comment.model';
import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { isTicketSupport } from 'src/app/models/ticket/ticket.util';
import * as fromStore from 'src/app/store/';
import { CommentService } from '../../services';

@Component({
  selector: 'app-tickets-detail-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './detail-shell.component.html',
  standalone: false,
})
export class TicketsDetailShellComponent {
  @Input() isLoading: boolean;

  private readonly store = inject(Store<fromStore.State>);

  private readonly commentService = inject(CommentService);

  readonly isTicketIdOnRouter =
    !!inject(ActivatedRoute).snapshot.paramMap.get('ticketId');

  readonly showDetails = signal(false);

  readonly fileUploadResult = signal<{
    files: { fileName: string; success: boolean }[];
  }>({ files: [] });

  private readonly ticketsLoaded = toSignal(
    this.store.select(fromStore.getTicketsLoaded),
    { initialValue: false },
  );

  readonly ticket$: Observable<Ticket> = this.isTicketIdOnRouter
    ? this.store.select(fromStore.getCurrentRouterTicket)
    : this.store.select(fromStore.getSelectedTicket);

  readonly ticket = toSignal(this.ticket$, {
    initialValue: null as Ticket | null,
  });

  readonly allowUserInput = computed(
    () =>
      this.ticket()?.Business_Purpose__c ===
      BusinessPurpose.NOCProactiveCustomerAlert,
  );

  readonly isNocTicket = computed(() => isTicketSupport(this.ticket()));

  constructor() {
    effect(() => {
      if (!this.ticketsLoaded()) {
        untracked(() => this.store.dispatch(new fromStore.LoadTickets()));
      }
    });

    effect(() => {
      const ticket = this.ticket();
      if (ticket?.Id) untracked(() => this.initialize());
    });
  }

  reloadTicketsData(): void {
    this.store.dispatch(new fromStore.LoadTickets());
  }

  fileUploaded(uploadResults: {
    files: { fileName: string; success: boolean }[];
  }): void {
    this.fileUploadResult.set(uploadResults);
  }

  initialize(): void {
    this.markCommentsAsRead();
    this.fileUploadResult.set({ files: [] });
  }

  markCommentsAsRead(): void {
    const t = this.ticket();
    if (
      !t?.Id ||
      !(t.Comments__r?.totalSize ?? 0) ||
      !this.hasUnreadComments(t.Comments__r?.records ?? [])
    ) {
      return;
    }
    firstValueFrom(
      this.commentService.markCommentsAsRead({ Case__c: t.Id }),
    ).catch(() => {});
  }

  hasUnreadComments = (comments: Comment[]): boolean =>
    comments.some((c) => !c.Is_Read__c);
}
